import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";

interface MasonryGridProps {
  children: React.ReactNode[];
  spacing?: number;
  maxColumns?: number; // Optional prop to limit max columns
}

const MasonryGrid: React.FC<MasonryGridProps> = ({
  children,
  spacing = 1.5,
  maxColumns,
}) => {
  const theme = useTheme();

  // More granular breakpoints for better responsiveness with odd numbers
  const is1920 = useMediaQuery("(min-width:1920px)"); // 8 columns
  const is1680 = useMediaQuery("(min-width:1680px)"); // 7 columns
  const is1400 = useMediaQuery("(min-width:1400px)"); // 6 columns
  const is1200 = useMediaQuery("(min-width:1200px)"); // 5 columns
  const is1000 = useMediaQuery("(min-width:1000px)"); // 4 columns
  const is800 = useMediaQuery("(min-width:800px)"); // 3 columns
  const is600 = useMediaQuery("(min-width:600px)"); // 3 columns
  const is480 = useMediaQuery("(min-width:480px)"); // 3 columns

  // Determine number of columns based on screen size (largest first)
  const getColumnCount = () => {
    let baseColumns;
    if (is1920)
      baseColumns = 6; // 1920px+ = 6 columns (reduced from 8)
    else if (is1680)
      baseColumns = 6; // 1680px+ = 6 columns (reduced from 7)
    else if (is1400)
      baseColumns = 5; // 1400px+ = 5 columns (reduced from 6)
    else if (is1200)
      baseColumns = 4; // 1200px+ = 4 columns (reduced from 5)
    else if (is1000)
      baseColumns = 4; // 1000px+ = 4 columns
    else if (is800)
      baseColumns = 3; // 800px+ = 3 columns
    else if (is600)
      baseColumns = 3; // 600px+ = 3 columns
    else if (is480)
      baseColumns = 3; // 480px+ = 3 columns
    else baseColumns = 2; // <480px = 2 columns

    // Apply maxColumns limit if provided
    return maxColumns ? Math.min(baseColumns, maxColumns) : baseColumns;
  };

  const columnCount = getColumnCount();
  const spacingValue = theme.spacing(spacing);

  // Distribute children across columns
  const columns = Array.from(
    { length: columnCount },
    () => [] as React.ReactNode[]
  );

  children.forEach((child, index) => {
    const columnIndex = index % columnCount;
    columns[columnIndex].push(child);
  });

  return (
    <Box
      sx={{
        display: "flex",
        gap: spacingValue,
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      {columns.map((column, columnIndex) => (
        <Box
          key={columnIndex}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: spacingValue,
            flex: 1,
            minWidth: 0, // Prevent flex items from growing beyond container
          }}
        >
          {column.map((child, itemIndex) => (
            <Box key={`${columnIndex}-${itemIndex}`}>{child}</Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default MasonryGrid;
